import { Logo, TFFLogo } from "@components/atoms/Logo"
import { Badge } from "@components/ui/Badge"
import { Button } from "@components/ui/Button"
import { Icon, IconCart, IconCross, IconMenu, IconPlanner } from "@components/ui/Icon"
import { useFerve } from "@features/ferve/FerveProvider"
import { FLYING_CARPET_PANELS, FlyingCarpetTab } from "@features/flying-carpet/carpet-panels"
import { useFlyingCarpet } from "@features/flying-carpet/useFlyingCarpet"
import { useSiteTheme } from "@features/header/header-theme"
import { SearchBoxDesktop } from "@features/search/SearchBox.Desktop"
import { SearchBoxMobile } from "@features/search/SearchBox.Mobile"
import { formatTffCenterStatus } from "@features/tff/tff-utils"
import { TFFMegaMenu } from "@features/tff/TFFMegaMenu"
import { ErrorBoundary } from "@features/tracking/ErrorBoundary"
import { trackEvent } from "@features/tracking/tracking"
import { useWishlistEnabled } from "@features/wishlist/useWishlist"
import { WishlistNotification } from "@features/wishlist/WishlistNotification"
import { useMegaMenu, useMegaMenuButtonProps } from "@hooks/useMegaMenu"
import { useTFF } from "@hooks/useTFF"
import * as D from "@radix-ui/react-dialog"
import { Slot } from "@radix-ui/react-slot"
import { cn } from "@utils/tw"
import { defineBlock, SlotBlocks } from "eddev/blocks"
import { useAppData } from "eddev/hooks"
import { Link, useHydrating, useRouterEvents } from "eddev/routing"
import { AnimatePresence, motion, MotionProps, useMotionValue, useSpring } from "motion/react"
import { ComponentProps, PropsWithChildren, useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useKey } from "react-use"
import { useSnapshot } from "valtio"

export const meta: BlockMeta = {
  title: "Site Header",
  description: "The header of the site",
  icon: "hammer",
  inserter: false,
  lazyLoad: false,
}

export default defineBlock("parts/part-site-header", (props) => {
  const theme = useSiteTheme().header

  const [fixedHeader, setFixedHeader] = useState(false)

  const menu = useMegaMenu()
  useRouterEvents((e) => {
    if (e.type === "clicked") {
      menu.hideMenu()
    }
  })

  useEffect(() => {
    if (env.admin) return
    const wrapperElement = document.getElementById("site-header")!
    const topBar = document.getElementById("site-header-top-bar")!
    const secondaryBar = document.getElementById("site-header-secondary-bar")!

    const update = () => {
      const wrapperBounds = wrapperElement.getBoundingClientRect()
      const topBounds = topBar?.getBoundingClientRect()
      const secondaryBounds = secondaryBar?.getBoundingClientRect()
      const headerHeight = wrapperBounds.height
      document.documentElement.style.setProperty("--spacing-headerHeight", `${headerHeight}px`)
      document.documentElement.style.setProperty("--spacing-flyingCarpetTop", `${topBounds.bottom - 1}px`)
      setFixedHeader(true)
    }

    const observer = new ResizeObserver(update)
    observer.observe(wrapperElement)
    observer.observe(topBar)
    observer.observe(secondaryBar)
    return () => {
      observer.disconnect()
    }
  }, [])

  const tff = useTFF()
  const festivalSlug = tff.isTFF ? "tff" : "sff"

  return (
    <>
      <header
        className={cn(
          "sticky top-0 z-[45]",
          theme === "dark" ? "theme-dark" : "theme-light",
          fixedHeader && "h-headerHeight",
          "pointer-events-auto",
        )}
        onPointerDown={(e) => {
          if (document.querySelector("route-drawer")) {
            e.stopPropagation()
            e.preventDefault()
          }
        }}
      >
        <div
          id="site-header"
          className={cn("bg-bg text-fg z-[45]", fixedHeader ? "fixed top-0 left-0 right-0" : "relative")}
        >
          {props.enableAlert && (
            <SlotBlocks
              id="notice"
              title="Alert Notice"
              template={[["parts/header-alert", {}]]}
              templateLock="insert"
            />
          )}
          <div
            className="border-b border-bg-mid"
            onPointerDown={() => {
              // Hide the menu if the user taps the top-bar, especially on mobile
              if (!env.admin) menu.hideMenu()
            }}
            id="site-header-top-bar"
          >
            <div className="mx-auto w-grid flex h-headerRowHeight items-center">
              {/* Logo */}
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={festivalSlug}
                  className="flex-1 flex h-[93%] sm:h-full items-start origin-left"
                  {...siteToggleProps}
                >
                  {festivalSlug === "sff" ? <SFFHeaderLogo /> : <TFFHeaderLogo />}
                </motion.div>
              </AnimatePresence>

              {/* Site-wide search */}
              <ErrorBoundary fallback={<div />}>
                <div className="hidden md:block justify-center flex-1 basis-auto">
                  <AnimatePresence>
                    {festivalSlug === "sff" && (
                      <motion.div className="flex w-full justify-center h-headerRowHeight" {...siteToggleProps}>
                        <SearchBoxDesktop />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </ErrorBoundary>

              {/* Function buttons (planner, cart etc) */}
              <div className="flex-1 flex gap-[0] sm:gap-2 justify-end">
                <FunctionButtons />
              </div>
            </div>
          </div>
          <div className="border-b border-bg-mid" id="site-header-secondary-bar">
            {/* Desktop Nav */}
            <div className="hidden sm:flex items-center justify-between w-grid mx-auto h-headerRowHeight origin-left">
              <AnimatePresence mode="wait">
                <motion.div
                  key={festivalSlug}
                  className={cn(!env.admin && "stack-x-3 origin-left")}
                  {...siteToggleProps}
                >
                  {festivalSlug === "sff" ? (
                    <SlotBlocks
                      id="menu-items"
                      title="Menu Items"
                      allowedBlocks={["parts/megamenu-page"]}
                      adminClassName="h-full flex gap-4 [&_>.block-list-appender]:static [&_.block-editor-button-block-appender]:text-white [&_.block-editor-button-block-appender]:h-full"
                      appender={{ type: "button2" }}
                      orientation="horizontal"
                    />
                  ) : (
                    <TFFDesktopMenu />
                  )}
                </motion.div>
              </AnimatePresence>
              <AnimatePresence mode="wait">
                <motion.div key={festivalSlug} className="flex gap-2 origin-right" {...siteToggleProps}>
                  <SlotBlocks
                    id={festivalSlug === "sff" ? "buttons" : "buttons-tff"}
                    title="Buttons"
                    allowedBlocks={["parts/header-button"]}
                    adminClassName="h-full flex gap-4 [&_>.block-list-appender]:static [&_.block-editor-button-block-appender]:text-white [&_.block-editor-button-block-appender]:h-full"
                    appender={{ type: "button2" }}
                    orientation="horizontal"
                  />
                  {festivalSlug === "tff" && (
                    <>
                      <Button shape="branded" variant="primary" href="/tff/support/">
                        Donate
                      </Button>
                    </>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
            {/* Mobile search */}
            <ErrorBoundary fallback={<div />}>
              <AnimatePresence mode="wait">
                {festivalSlug === "sff" ? (
                  <motion.div key="sff" {...siteToggleProps} className="sm:hidden w-grid mx-auto py-4">
                    <SearchBoxMobile />
                  </motion.div>
                ) : (
                  <motion.div key="tff" {...siteToggleProps} className="sm:hidden w-grid mx-auto py-3">
                    <TFFMobileMenu />
                  </motion.div>
                )}
              </AnimatePresence>
            </ErrorBoundary>
          </div>
          {env.admin && <MegaMenuAdmin />}
          {!env.admin && <MegaMenuDropdown />}
        </div>
      </header>
    </>
  )
})

function MegaMenuDropdown() {
  const ctx = useMegaMenu()
  const sizer = useRef<HTMLDivElement>(null)
  const heightTarget = useMotionValue(0)
  const heightSpring = useSpring(heightTarget, { damping: 60, stiffness: 500, bounce: 0 })

  const update = () => {
    setTimeout(() => {
      const value = ctx.activeItem ? sizer.current?.getBoundingClientRect().height : 0
      heightTarget.set(value ?? 0)
    }, 10)
  }

  // When hitting the escape key, focus the active item and hide the menu
  useKey(
    "Escape",
    (e) => {
      if (ctx.activeItem) {
        const element = document.querySelector(`[data-keyboard-id="${ctx.activeItem}"]`) as HTMLElement
        element?.focus()
        e.preventDefault()
      }
      ctx.hideMenu()
    },
    { event: "keydown" },
    [ctx.activeItem],
  )

  useEffect(() => {
    update()
  }, [ctx.activeItem])

  const ssr = useHydrating()

  if (ssr) {
    return null
  }

  return (
    <>
      {createPortal(
        <AnimatePresence>
          {ctx.activeItem && (
            <motion.div
              className="radix-backdrop z-40 bg-[rgba(0,0,0,0.3)] will-change-transform"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onPointerDown={() => ctx.hideMenu()}
            />
          )}
        </AnimatePresence>,
        document.body,
      )}
      <motion.div
        className="relative w-full"
        onPointerEnter={() => ctx.cancelHide()}
        onPointerLeave={() => ctx.hideSoon()}
      >
        <motion.div
          className="absolute top-0 left-0 right-0 bg-bg will-change-transform overflow-hidden"
          style={{ height: heightSpring }}
          layout="size"
        >
          <div ref={sizer}>
            <AnimatePresence mode="wait">
              {ctx.activeItem && (
                <motion.div
                  key={ctx.activeItem}
                  initial="closed"
                  animate="open"
                  exit="closed"
                  onAnimationStart={update}
                  onAnimationEnd={update}
                >
                  <AutoFocusFirstItem enabled={ctx.wasKeyboardActivated}>{ctx.activeItemContent}</AutoFocusFirstItem>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </motion.div>
    </>
  )
}

function AutoFocusFirstItem({ children, enabled }: { children: React.ReactNode; enabled: boolean }) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (enabled) {
      const firstLink = ref.current?.querySelector("a, button") as HTMLElement
      firstLink?.focus()
    }
  }, [])

  return (
    <div ref={ref} className="contents">
      {children}
    </div>
  )
}

function MegaMenuAdmin() {
  const ctx = useMegaMenu()

  return <div className="w-full" ref={ctx.setAdminRenderTarget}></div>
}

function FunctionButtons() {
  return (
    <>
      <Cart />
      <Planner />
      <Account />
      <MenuButton />
    </>
  )
}

function SFFHeaderLogo() {
  const Comp = env.admin ? "div" : Link
  return (
    <Comp
      href="/"
      className="flex h-full py-4 items-stretch gap-3 animate-in max-sm:active:scale-95 max-sm:active:duration-100 duration-200"
      data-mixpanel-area="Header Logo"
      title="Return to Homepage"
    >
      <Logo className="h-full block" />
      <SlotBlocks
        id="date"
        title="Date Lock"
        template={[["parts/header-date-lockup", {}]]}
        templateLock="all"
        adminClassName="h-full flex"
      />
    </Comp>
  )
}

function TFFHeaderLogo() {
  const Comp = env.admin ? "div" : Link
  return (
    <Comp
      href="/tff/"
      className="flex h-full py-4 items-stretch gap-3 animate-in max-sm:active:scale-95 max-sm:active:duration-100 duration-200"
      data-mixpanel-area="Header Logo"
      title="Return to Homepage"
    >
      <TFFLogo />
    </Comp>
  )
}

function CarpetTrigger(
  props: PropsWithChildren<{
    tab: FlyingCarpetTab
  }>,
) {
  const carpet = useFlyingCarpet()
  return (
    <Slot
      data-state={carpet.isOpen && carpet.tab?.startsWith(props.tab) ? "open" : "closed"}
      onPointerDown={(e) => {
        if (carpet.isOpen && carpet.tab !== props.tab) {
          e.stopPropagation()
        } else if (carpet.isOpen && carpet.tab === props.tab) {
          e.stopPropagation()
          e.preventDefault()
          carpet.close()
          return
        }
        trackEvent(`Open Flying Carpet`, {
          Area: "Header UI",
          Panel: FLYING_CARPET_PANELS[props.tab].title,
        })
        carpet.launch(props.tab)
      }}
      id={"flying-carpet-anchor-" + props.tab}
      className={carpet.isOpen ? "pointer-events-auto" : ""}
    >
      {props.children}
    </Slot>
  )
}

function Cart() {
  const hydrating = useHydrating()
  const total = env.admin ? 0 : (useSnapshot(useFerve().cart.state).data?.totalTickets ?? 0)
  const ref = useRef<SVGSVGElement>(null)

  useEffect(() => {
    ref.current?.animate(
      [
        { transform: "scale(1)" },
        { transform: "scale(1.5)", easing: "ease-out" },
        { transform: "scale(1)", easing: "ease-in" },
      ],
      {
        duration: 600,
        iterations: 1,
        delay: 500,
      },
    )
  }, [total])

  return (
    <Badge enabled={!hydrating && total > 0} dot>
      <CarpetTrigger tab="cart">
        <Button shape="icon" variant="ghost" icon={<IconCart data-part="icon" ref={ref} />} aria-label="Open Cart" />
      </CarpetTrigger>
    </Badge>
  )
}

function Planner() {
  const enabled = useWishlistEnabled()
  const hydrating = useHydrating()
  const total = env.admin ? 0 : useSnapshot(useFerve().wishlist.state).hashes.size
  const ref = useRef<SVGSVGElement>(null)

  useEffect(() => {
    ref.current?.animate(
      [
        { transform: "scale(1)" },
        { transform: "scale(1.5)", easing: "ease-out" },
        { transform: "scale(1)", easing: "ease-in" },
      ],
      {
        duration: 600,
        iterations: 1,
        delay: 500,
      },
    )
  }, [total])

  if (!enabled) {
    return null
  }

  return (
    <Badge enabled={!hydrating && total > 0} dot>
      <CarpetTrigger tab="planner">
        <WishlistNotification>
          <Button
            shape="icon"
            variant="ghost"
            icon={<IconPlanner data-part="icon" ref={ref} />}
            aria-label="Open Planner"
          />
        </WishlistNotification>
      </CarpetTrigger>
    </Badge>
  )
}

function Account() {
  return (
    <CarpetTrigger tab="account">
      <Button shape="icon" variant="ghost" icon="account" aria-label="Open Account" />
    </CarpetTrigger>
  )
}

function MenuButton() {
  const carpet = useFlyingCarpet()
  const isOpen = carpet.isOpen && carpet.tab === "menu"
  return (
    <CarpetTrigger tab="menu">
      <Button
        shape="icon"
        aria-label="Main Menu"
        icon={
          <AnimatePresence initial={false}>
            <motion.div
              key={isOpen ? "open" : "closed"}
              initial={{ opacity: 0, scale: 0.2 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.2 }}
              className="absolute inset-0 flex items-center justify-center"
            >
              {isOpen ? <IconCross /> : <IconMenu />}
            </motion.div>
          </AnimatePresence>
        }
      />
    </CarpetTrigger>
  )
}

function TFFDesktopMenu() {
  const ctx = useMegaMenu()
  const tff = useTFF()
  const buttonProps = useMegaMenuButtonProps("tff-mega-menu", <TFFMegaMenu />)

  return (
    <div className="flex items-center gap-3">
      <TFFSelector isOpen={ctx.activeItem === "tff-mega-menu"} {...buttonProps} />
      <TFFCenterLinks />
    </div>
  )
}

function TFFCenterLinks() {
  const tff = useTFF()
  return (
    <AnimatePresence>
      {tff.centerSlug && (
        <motion.div {...siteToggleProps} className="flex items-center h-control">
          <div className="h-control w-px bg-bg-mid mr-3" />
          <Button variant="link" href={`/tff/program/${tff.centerSlug}/`}>
            Program
          </Button>
          <Button variant="link" href={`/tff/tickets/${tff.centerSlug}/`}>
            Tickets
          </Button>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

function TFFMobileMenu() {
  const ctx = useMegaMenu()
  const tff = useTFF()
  const [isOpen, setIsOpen] = useState(false)

  const carpet = useFlyingCarpet()
  useEffect(() => {
    if (carpet.isOpen) {
      setIsOpen(false)
    }
  }, [carpet.isOpen])

  useRouterEvents((e) => {
    if (e.type === "clicked") {
      setIsOpen(false)
    }
  })

  return (
    <>
      <div className="flex items-center gap-3">
        <TFFSelector
          isOpen={isOpen}
          onClick={() => {
            setIsOpen(!isOpen)
          }}
        />
        <TFFCenterLinks />
      </div>
      <D.Root open={isOpen} onOpenChange={setIsOpen} modal>
        <AnimatePresence>
          {isOpen && (
            <motion.div
              variants={{ open: { opacity: 1 }, closed: { opacity: 0, transition: { delay: 0.1 } } }}
              initial="closed"
              animate="open"
              exit="closed"
              className="fixed z-[90] left-0 right-0 top-headerHeight overflow-hidden h-[calc(100lvh+200px)] bg-bg outline-none pointer-events-none"
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 40,
                restDelta: 0.001,
                restSpeed: 0.001,
              }}
            >
              <motion.div
                variants={{
                  open: { opacity: 1, y: 0 },
                  closed: { opacity: 0, y: -50 },
                }}
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 40,
                  restDelta: 0.001,
                  restSpeed: 0.001,
                }}
                className="absolute top-0 right-0 left-0 h-[calc(100dvh-var(--spacing-headerHeight))] overflow-scroll overflow-x-hidden overscroll-contain outline-none px-grid-margin pointer-events-auto"
              >
                <TFFMegaMenu />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </D.Root>
    </>
  )
}

function TFFSelector({ isOpen, ...props }: { isOpen: boolean } & ComponentProps<"button">) {
  const { centerSlug } = useTFF()
  const center = useAppData((data) => data.tffCenters?.nodes.find((center) => center.slug === centerSlug))

  const centerInfo = center?.centerInfo ? formatTffCenterStatus(center?.centerInfo) : null

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        key={center ? "selected" : "none"}
      >
        {center ? (
          <button
            className="text-left min-h-control flex gap-3 group/tff"
            aria-label={`Change location, ${center.title} is selected`}
            {...props}
            data-open={isOpen ? "" : undefined}
          >
            <div className="flex flex-col items-start justify-center type-ui-l gap-[0.1em]">
              <span>{center.title}</span>
              <span className="type-ui-s opacity-50">{centerInfo?.datesText}</span>
            </div>
            <div className="flex items-center transition-colors justify-center group-hover/tff:bg-bg-low group-data-[open]/tff:bg-bg-low group-active/tff:bg-bg-mid group-active/tff:transition-none size-control flex-1 rounded-[12px]">
              <Icon name="chevronDown" />
            </div>
          </button>
        ) : (
          <>
            <Button
              className="-ml-4 type-ui-l"
              rightIcon="chevronDown"
              variant={isOpen ? "default" : "ghost"}
              {...props}
            >
              Choose Location
            </Button>
          </>
        )}
      </motion.div>
    </AnimatePresence>
  )
}

const siteToggleProps: MotionProps = {
  initial: { opacity: 0, scale: 0.95 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.95 },
}
